import { Injectable } from '@angular/core';
import { combineLatest } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { map } from 'rxjs/operators';

import { AsyncData } from '@cigna/core/interfaces';
import { ClaimsSearchPartialState } from './claims-search.reducer';
import { claimsSearchQuery } from './claims-search.selectors';
import {
  TabSelection,
  SearchClaims,
  ModifySearch,
  NewSearch,
  ClaimsNumberSearch,
  DisplayClaimsDetails,
  MultiplePatients,
  BackToMultiplePatients,
  ResetSearch,
  RemittanceReadStatus,
  FetchAdjustments,
  SetIsReconsiderationHistoryOpened,
  SetSupportingInformationTabOpened,
  SetHistoryDataIndex,
  GetProviderByTin,
  SaveStartAReconsiderationData,
  RemoveAdjustment,
  GetSupportingInformationHistory,
  GetSupportingInformationAttachments,
  UploadAttachments,
  GetAttachmentsSignedUrlToPutFile,
  AttachmentClick,
  RemoveAttachment,
  SubmitSupportingInformation,
  SetSupportingInfoSubmissionNumber,
  SetSelectedServiceLine,
  RemoveSelectedServiceLine,
  FetchSupplementaryClaimInfo,
  ResetReconsiderationInfo,
} from './claims-search.actions';
import {
  ClaimsSearchCriteria,
  ClaimsReferSearchCriteria,
  RemittanceRead,
  ClaimTabType,
  DetailRequest,
  ServiceLineInfo,
  Adjustment,
} from '@cigna/chcp/claims/shared/util';
import {
  Attachment,
  FileWithUrl,
  ReconsiderationType,
} from '@cigna/chcp/shared/util';
import { FlaggedClaim } from '@cigna/chcp/shared/user-profile-data-access';

@Injectable({
  providedIn: 'root',
})
export class ClaimsSearchFacade {
  providerLoadingState$ = this.store$.pipe(
    select(claimsSearchQuery.getProviderLoadingState),
  );

  providerLoadingError$ = this.store$.pipe(
    select(claimsSearchQuery.getProviderLoadingError),
  );

  searchMemberCriteria$ = this.store$.pipe(
    select(claimsSearchQuery.getMemberSearchCriteria),
  );
  searchClaimCriteria$ = this.store$.pipe(
    select(claimsSearchQuery.getClaimsSearchCriteria),
  );
  selectedTab$ = this.store$.pipe(select(claimsSearchQuery.getTabSelection));
  searchStage$ = this.store$.pipe(select(claimsSearchQuery.getSearchStage));
  searchResults$ = this.store$.pipe(
    select(claimsSearchQuery.getClaimsSearchResults),
  );
  error$ = this.store$.pipe(select(claimsSearchQuery.getError));
  claimDetails$ = this.store$.pipe(select(claimsSearchQuery.getClaimDetails));
  claimDetailsClaimInfo$ = this.store$.pipe(
    select(claimsSearchQuery.getClaimDetailsClaimInfo),
  );
  claimDetailsMemberInfo$ = this.store$.pipe(
    select(claimsSearchQuery.getClaimDetailsMemberInfo),
  );
  claimsDDAR$ = this.store$.pipe(select(claimsSearchQuery.getClaimsDDAR));
  memberLists$ = this.store$.pipe(select(claimsSearchQuery.getMemberLists));
  isMultiplePatients$ = this.store$.pipe(
    select(claimsSearchQuery.getIsMultiplePatients),
  );
  memberKey$ = this.store$.pipe(select(claimsSearchQuery.getUniqueIdentifier));
  serviceLineInfo$ = this.store$.pipe(
    select(claimsSearchQuery.getServiceLineInfo),
  );

  adjustments$ = combineLatest([
    this.store$.select(claimsSearchQuery.getAdjustmentsLoading),
    this.store$.select(claimsSearchQuery.getAdjustments),
    this.store$.select(claimsSearchQuery.getAdjustmentsError),
  ]).pipe(
    map(([loading, data, error]) => new AsyncData(!loading, data, error)),
  );

  supportingInformationHistory$ = combineLatest([
    this.store$.select(
      claimsSearchQuery.getSupportingInformationHistoryLoading,
    ),
    this.store$.select(claimsSearchQuery.getSupportingInformationHistory),
    this.store$.select(claimsSearchQuery.getSupportingInformationHistoryError),
  ]).pipe(
    map(([loading, data, error]) => new AsyncData(!loading, data, error)),
  );

  supportingInformationUploaded$ = this.store$.select(
    claimsSearchQuery.getSupportingInformationUploaded,
  );

  isReconsiderationHistoryOpened$ = this.store$.pipe(
    select(claimsSearchQuery.isReconsiderationHistoryOpened),
  );

  isSupportingInformationTabOpened$ = this.store$.pipe(
    select(claimsSearchQuery.isSupportingInformationTabOpened),
  );

  isAccountRestricted$ = this.store$.pipe(
    select(claimsSearchQuery.isAccountRestricted),
  );
  isGroupRestricted$ = this.store$.pipe(
    select(claimsSearchQuery.isGroupRestricted),
  );
  claimRefNum$ = this.store$.pipe(select(claimsSearchQuery.getClaimRefNum));
  selectedHistoryData$ = this.store$.pipe(
    select(claimsSearchQuery.getHistoryData),
  );
  providers$ = this.store$.pipe(select(claimsSearchQuery.getProviders));
  startAReconsiderationData$ = this.store$.pipe(
    select(claimsSearchQuery.getStartAReconsiderationFormData),
  );

  supportingInformationSubmssionId$ = this.store$.pipe(
    select(claimsSearchQuery.getSupportingInformationSubmssionId),
  );

  supportingInformationSubmitLoading$ = this.store$.pipe(
    select(claimsSearchQuery.getSupportingInformationSubmitLoading),
  );

  supportingInformationSubmitError$ = this.store$.pipe(
    select(claimsSearchQuery.getSupportingInformationSubmitError),
  );

  selectedServiceLines$ = this.store$.pipe(
    select(claimsSearchQuery.getSelectedServiceLines),
  );

  constructor(private store$: Store<ClaimsSearchPartialState>) {}

  tabSwitch(selectedTab: ClaimTabType) {
    this.store$.dispatch(new TabSelection(selectedTab));
  }

  submitSearch(formValue: ClaimsSearchCriteria) {
    this.store$.dispatch(new SearchClaims(formValue));
  }

  claimRefSubmitSearch(formValue: ClaimsReferSearchCriteria) {
    this.store$.dispatch(new ClaimsNumberSearch(formValue));
  }

  modifySearch() {
    this.store$.dispatch(new ModifySearch());
  }

  newSearch() {
    this.store$.dispatch(new NewSearch());
  }

  claimDetails(detailRequest: DetailRequest) {
    this.store$.dispatch(new DisplayClaimsDetails(detailRequest));
  }

  multiplePatientsSubmit(
    formValue: ClaimsSearchCriteria,
    flaggedData?: FlaggedClaim,
  ) {
    this.store$.dispatch(new MultiplePatients(formValue, flaggedData));
  }

  backToMultiplePatients() {
    this.store$.dispatch(new BackToMultiplePatients());
  }

  setIntialState() {
    this.store$.dispatch(new ResetSearch());
  }

  fetchReadStatus(payload: RemittanceRead) {
    this.store$.dispatch(new RemittanceReadStatus(payload));
  }

  fetchAdjustments(claimReferenceNumber: string) {
    this.store$.dispatch(new FetchAdjustments({ claimReferenceNumber }));
  }

  fetchSupplementaryClaimInfo() {
    this.store$.dispatch(new FetchSupplementaryClaimInfo());
  }

  fetchSupportingInformation(
    claimReferenceNumber: string,
    sourceSystemCode: string,
    claimEngineClaimID: string,
  ) {
    this.store$.dispatch(
      new GetSupportingInformationHistory({
        claimReferenceNumber,
        sourceSystemCode,
        claimEngineClaimID,
      }),
    );
  }

  fetchSupportingInformationAttachments(id: string, attachmentIds: string[]) {
    this.store$.dispatch(
      new GetSupportingInformationAttachments({ id, attachmentIds }),
    );
  }

  uploadSupportingInformation(files: FileWithUrl[]) {
    this.store$.dispatch(new UploadAttachments(files));
    const validFiles = files.filter((v) => !v.error);
    if (validFiles?.length) {
      this.store$.dispatch(new GetAttachmentsSignedUrlToPutFile(validFiles));
    }
  }

  submitSupportingInformation() {
    this.store$.dispatch(new SubmitSupportingInformation());
  }

  removeAdjustment(id: string, reconType: ReconsiderationType) {
    this.store$.dispatch(new RemoveAdjustment({ id, reconType }));
  }

  setReconsiderationHistoryOpened(isOpened: boolean): void {
    this.store$.dispatch(new SetIsReconsiderationHistoryOpened(isOpened));
  }

  setSupportingInformationTabOpened(isOpened: boolean): void {
    this.store$.dispatch(new SetSupportingInformationTabOpened(isOpened));
  }

  setHistoryDataIndex(i: number): void {
    this.store$.dispatch(new SetHistoryDataIndex(i));
  }

  resetReconsiderationInfo(): void {
    this.store$.dispatch(new ResetReconsiderationInfo());
  }

  loadProvidersByTin(tin: string) {
    this.store$.dispatch(new GetProviderByTin(tin));
  }

  saveStartAReconsiderationForm(payload: Partial<Adjustment>): void {
    this.store$.dispatch(new SaveStartAReconsiderationData(payload));
  }

  setSupportingInfoSubmissionNumber(id: string) {
    this.store$.dispatch(new SetSupportingInfoSubmissionNumber(id));
  }

  clickSupInfoAttachment(attachment: Attachment): void {
    this.store$.dispatch(new AttachmentClick(attachment));
  }

  removeSupInfoAttachment(fileName: string) {
    this.store$.dispatch(new RemoveAttachment(fileName));
  }

  setSelectedServiceLine(payload: ServiceLineInfo) {
    this.store$.dispatch(new SetSelectedServiceLine(payload));
  }

  removeSelectedServiceLine(payload: number) {
    this.store$.dispatch(new RemoveSelectedServiceLine(payload));
  }
}
